import { Box, Button, Typography } from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import config from "../../config/config";
import { CONFIRMATION_DELAY } from "../../constants/const";
import { HelperBotType, RankType } from "../../constants/enum/enum";
import { HelperBots } from "../../constants/helper";
import { Messages } from "../../constants/messages";
import { getGameBankAllowance } from "../../helper/contractFunctions/MafiaGameBank";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import {
  calculateBulletBotGeneratedBulletAmount,
  calculateBulletBotRequiredCashAmount,
} from "../../helper/helperbot";
import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import {
  approveGameCashAction,
  endBoozeBotAction,
  endBulletDealerBotAction,
  endCarBotAction,
  endCrimeBotAction,
  endKSBotAction,
  endNarcsBotAction,
  getUserHelperBotInfo,
} from "../../reducers/game.slice";
import { getBulletBalance } from "../../reducers/profile.slice";
import {
  convertDateTime,
  toastInfo,
  toastSuccess,
  toUSDFormat,
} from "../../utils/utils";

import useStyles from "./index.styles";

export interface HelperBotItemProps {
  imageUrl: string;
  name: string;
  status?: string;
  description: string;
  rank: RankType;
  timeRemaining?: string;
  credits: number;
  isStarted: boolean;
  remainingCredit: number;
  botType: HelperBotType;
  cashReward: boolean;
  bulletReward?: boolean;
  handleWithdrawBot: (botType: HelperBotType, isAccepting?: boolean) => void;
  handleStartBot: (botType: HelperBotType) => void;
}

export const getRankColor = (rank: RankType): string => {
  switch (rank) {
    case "Low":
      return "rgb(51 135 55)";
    case "Medium":
      return "rgb(155, 114, 47)";
    case "High":
      return "rgb(198, 40, 40)";
    default:
      return "grey";
  }
};

const HelperBotItem = ({
  imageUrl,
  name,
  status,
  description,
  rank,
  timeRemaining,
  credits,
  isStarted,
  botType,
  remainingCredit,
  handleWithdrawBot,
  handleStartBot,
  cashReward,
  bulletReward,
}: HelperBotItemProps) => {
  const { classes } = useStyles();

  const { userBulletBotInfo, userBulletBotPlusInfo } = useAppSelector(
    (state) => state.game
  );

  const cashAmount = calculateBulletBotRequiredCashAmount(
    userBulletBotInfo,
    userBulletBotPlusInfo
  );

  const bulletAmount = calculateBulletBotGeneratedBulletAmount(
    userBulletBotInfo,
    userBulletBotPlusInfo
  );

  return (
    <Box className={classes.helperBotListItem}>
      <Box
        component="img"
        src={imageUrl}
        alt={name}
        className={classes.helperBotListItemImage}
      />

      <Box className={classes.helperBotListItemContent}>
        <Box className={classes.helperBotListItemDetail}>
          <Box className={classes.helperBotListItemTitle}>
            <Box component="img" src="/assets/imgs/inventory/helperitem.png" />
            <Typography className={classes.helperBotListItemTitleText}>
              {name}
            </Typography>
            {status && (
              <Typography className={classes.helperBotListItemStatus}>
                {status}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography
              fontFamily="Philosopher"
              color="grey"
              fontSize={14}
              fontWeight={700}
              maxWidth={600}
              className={classes.helperBotDescription}
            >
              {description}
            </Typography>
          </Box>

          <Box className={classes.helperBotListItemInfo}>
            <Box className={classes.helperBotListItemRank}>
              <Typography
                fontFamily="Philosopher"
                color="grey"
                fontSize={14}
                fontWeight={700}
              >
                {cashReward
                  ? "Rank & Cash reward: "
                  : bulletReward
                  ? "Bullet Reward: "
                  : "Kill Skill Xp: "}
              </Typography>
              <Typography
                fontFamily="Philosopher"
                fontSize={14}
                fontWeight={700}
                color={getRankColor(rank)}
              >
                {rank}
              </Typography>
            </Box>

            {timeRemaining && (
              <Box className={classes.helperBotListItemTime}>
                <Typography
                  fontFamily="Philosopher"
                  color="grey"
                  fontSize={14}
                  fontWeight={700}
                >
                  Est time remaining:
                </Typography>
                <Typography
                  fontFamily="Philosopher"
                  fontSize={14}
                  fontWeight={700}
                  color="grey"
                >
                  {timeRemaining}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box className={classes.helperBotListItemAction}>
          {isStarted &&
          remainingCredit === 0 &&
          botType === HelperBotType.BulletDealerBot ? (
            <Box className={classes.helperBotListItemCreditInfos}>
              <Box className={classes.helperBotListItemCredit}>
                <Box className={classes.helperBotListItemCashAmount}>
                  <Box
                    component={"img"}
                    src="/assets/imgs/inventory/bulletitem.png"
                  ></Box>

                  {toUSDFormat(bulletAmount)}
                </Box>

                <Typography className={classes.helperBotCashRequiredLabel}>
                  Generated Bullet Amount:
                </Typography>
              </Box>

              <Box className={classes.helperBotListItemCredit}>
                <Box className={classes.helperBotListItemCashAmount}>
                  <Box
                    component={"img"}
                    src="/assets/imgs/layout/currency.png"
                  ></Box>

                  {toUSDFormat(cashAmount)}
                </Box>

                <Typography className={classes.helperBotCashRequiredLabel}>
                  Required cash Amount:
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box className={classes.helperBotListItemCredit}>
              <Box className={classes.helperBotListItemCreditCount}>
                {isStarted ? remainingCredit : credits}
              </Box>

              {!(isStarted && botType === HelperBotType.BulletDealerBot) && (
                <Typography className={classes.helperBotListItemCreditLabel}>
                  {isStarted ? "Credits remaining" : "Credit cost per attempt"}
                </Typography>
              )}
            </Box>
          )}

          {isStarted ? (
            <Box className={classes.helperBotActionButtonList}>
              {botType === HelperBotType.BulletDealerBot && (
                <Button
                  className={classes.helperBotListItemWithdrawButton}
                  onClick={() => {
                    handleWithdrawBot(botType, false);
                  }}
                  disabled={remainingCredit !== 0}
                >
                  Cancel bot
                </Button>
              )}

              <Button
                className={classes.helperBotListItemWithdrawButton}
                onClick={() => {
                  handleWithdrawBot(botType);
                }}
                disabled={remainingCredit !== 0}
              >
                Withdraw bot
              </Button>
            </Box>
          ) : (
            <Button
              className={classes.helperBotListItemHireButton}
              onClick={() => {
                handleStartBot(botType);
              }}
            >
              Hire now!
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const HelperList = ({
  handleCurrentSelectedBotChanged,
}: {
  handleCurrentSelectedBotChanged: (botType: HelperBotType) => void;
}) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const currentTime = useCurrentTime();
  const { account } = useWallet();

  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { cashBalance } = useAppSelector((state) => state.profile);

  const { updateBalance } = useGameBankBalance(account, signMsg, signature);

  const {
    userCrimeBotInfo,
    userCarBotInfo,
    userKSBotInfo,
    userNarcsBotInfo,
    userBoozeBotInfo,
    userBulletBotInfo,
    userBulletBotPlusInfo,
  } = useAppSelector((state) => state.game);

  const handleStartBot = (botType: HelperBotType) => {
    handleCurrentSelectedBotChanged(botType);
  };

  const helperBotInfos = [
    userCrimeBotInfo,
    userCarBotInfo,
    userBoozeBotInfo,
    userNarcsBotInfo,
    userKSBotInfo,
    userBulletBotInfo,
  ];

  const handleWithdrawBot = async (
    botType: HelperBotType,
    isAccepting = true
  ) => {
    if (botType === HelperBotType.CrimeBot) {
      dispatchTxAction(
        dispatch,
        endCrimeBotAction(account),
        () => {
          toastSuccess(Messages.HELPER_BOT.CRIME_BOT.WITHDRAW_SUCCESS);
          dispatch(getUserHelperBotInfo(account));
        },
        CONFIRMATION_DELAY
      );
    } else if (botType === HelperBotType.GrandTheftAutoBot) {
      dispatchTxAction(
        dispatch,
        endCarBotAction({ account, signMsg, signature }),
        () => {
          toastSuccess(Messages.HELPER_BOT.CAR_BOT.WITHDRAW_SUCCESS);
          dispatch(getUserHelperBotInfo(account));
        },
        CONFIRMATION_DELAY
      );
    } else if (botType === HelperBotType.ShootingPracticeBot) {
      dispatchTxAction(
        dispatch,
        endKSBotAction(account),
        () => {
          toastSuccess(Messages.HELPER_BOT.KS_BOT.WITHDRAW_SUCCESS);
          dispatch(getUserHelperBotInfo(account));
        },
        CONFIRMATION_DELAY
      );
    } else if (botType === HelperBotType.BoozeSmugglingBot) {
      dispatchTxAction(
        dispatch,
        endBoozeBotAction({ account, signMsg, signature }),
        () => {
          toastSuccess(Messages.HELPER_BOT.BOOZE_BOT.WITHDRAW_SUCCESS);
          dispatch(getUserHelperBotInfo(account));
        },
        CONFIRMATION_DELAY
      );
    } else if (botType === HelperBotType.NarcoticsSmugglingBot) {
      dispatchTxAction(
        dispatch,
        endNarcsBotAction({ account, signMsg, signature }),
        () => {
          toastSuccess(Messages.HELPER_BOT.NARCS_BOT.WITHDRAW_SUCCESS);
          dispatch(getUserHelperBotInfo(account));
        },
        CONFIRMATION_DELAY
      );
    } else if (botType === HelperBotType.BulletDealerBot) {
      const cashAmount = calculateBulletBotRequiredCashAmount(
        userBulletBotInfo,
        userBulletBotPlusInfo
      );

      if (cashBalance < cashAmount && isAccepting) {
        toastInfo(Messages.HELPER_BOT.BULLET_DEALER_BOT.INSUFFICIENT_CASH);
        return;
      }

      const endBulletBot = () => {
        dispatchTxAction(
          dispatch,
          endBulletDealerBotAction({
            isAccepting,
            account,
            signMsg,
            signature,
          }),
          () => {
            toastSuccess(
              Messages.HELPER_BOT.BULLET_DEALER_BOT.WITHDRAW_SUCCESS
            );
            dispatch(getBulletBalance({ account, signMsg, signature }));
            dispatch(getUserHelperBotInfo(account));

            updateBalance(account, signMsg, signature);
          },
          CONFIRMATION_DELAY
        );
      };

      const allowance = await getGameBankAllowance(
        account,
        config.inventoryAddress
      );

      if (!isAccepting || cashAmount < allowance) {
        endBulletBot();
      } else {
        dispatchTxAction(
          dispatch,
          approveGameCashAction({
            cashAmount: 10 ** 18,
            account,
            spender: config.helperBotAddress,
          }),
          () => {
            endBulletBot();
          }
        );
      }
    }
  };

  return (
    <Box className={classes.helperBotList}>
      {HelperBots.map((bot, index) => {
        const remainingCredit = Math.max(
          Math.ceil(
            ((helperBotInfos[index]?.endTimestamp - currentTime) /
              (helperBotInfos[index]?.endTimestamp -
                helperBotInfos[index]?.startTimestamp)) *
              bot.credits *
              helperBotInfos[index].attemptCount
          ),
          0
        );

        return (
          <HelperBotItem
            key={index}
            {...bot}
            status={helperBotInfos[index]?.isRunning ? "In progress" : ""}
            timeRemaining={
              helperBotInfos[index]?.endTimestamp - currentTime > 0
                ? convertDateTime(
                    helperBotInfos[index]?.endTimestamp - currentTime
                  )
                : ""
            }
            botType={bot.id}
            remainingCredit={remainingCredit}
            isStarted={helperBotInfos[index]?.isRunning}
            handleWithdrawBot={handleWithdrawBot}
            handleStartBot={handleStartBot}
          />
        );
      })}
    </Box>
  );
};

export default HelperList;
